import React, { useState } from "react";
import { useTranslation } from "utilities/i18n";
import { OperationContext } from "@urql/core";
import {
  Omit,
  useConsolidateAndRenameAccountsMutation,
  useDeviceCountsQuery,
  useDualTenantUserCountsQuery,
  useTenantsListQuery,
  useUsersListQuery
} from "generated/graphql";
import { useHistory } from "polyrouter";
import { PolyIcon, CancelButton, SubmitButton } from "elements";
import { ModalForm, useModalDispatch } from "collections/Modal";
import { unknownNamePlaceholder } from "collections/Menu";
import { DISMISS, useNotification } from "collections/Notifications";
import { constructFilterList } from "utilities/filters/deviceFilters";
import { GenericBucket, isArrayOfGenericBucket } from "routes/Home/bucketTypes";
import { UseQueryArgs } from "polygraphql";
import { Form, Input, StandardFormSelect } from "collections/Form";
import { object, InferType, string } from "yup";
import * as yup from "yup";
import { AccountForTable } from "../Accounts";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

const NO_COUNT = -1;
const MODE_SELECT = "select";
const MODE_REVIEW = "review";

const initialDisplayMode: string = MODE_SELECT;
export type SetDisplayModeType = (value: string) => void;

export interface AccountSelectionInfo {
  fromAccountId: string | undefined;
  toAccountId: string | undefined;
  accountName: string | undefined;
}

const initialAccountSelectionInfo: AccountSelectionInfo = {
  fromAccountId: undefined,
  toAccountId: undefined,
  accountName: ""
};
export type SetAccountSelectionInfoType = (value: AccountSelectionInfo) => void;

const displayCount = (count: number): string => {
  return count >= 0 ? count.toString() : "\u2014";
};

const RefreshOnly: Partial<OperationContext> = {
  pause: true,
  requestPolicy: "network-only"
};

// entry point from app router
export const ConsolidateAccounts: React.FC<{ accounts: AccountForTable[] }> = ({
  accounts
}) => {
  const [displayMode, setDisplayMode] = useState(initialDisplayMode);
  const [accountSelectionInfo, setAccountSelectionInfo] = useState(
    initialAccountSelectionInfo
  );
  const isSelect = displayMode === MODE_SELECT;
  let currentView;

  if (isSelect) {
    currentView = (
      <SelectView
        setDisplayMode={setDisplayMode}
        accountSelectionInfo={accountSelectionInfo}
        setAccountSelectionInfo={setAccountSelectionInfo}
        accounts={accounts}
      />
    );
  } else {
    const { accountName, fromAccountId, toAccountId } = accountSelectionInfo;
    if (accountName && fromAccountId && toAccountId) {
      currentView = (
        <ReviewView
          setDisplayMode={setDisplayMode}
          accountSelectionInfo={{ accountName, fromAccountId, toAccountId }}
          setAccountSelectionInfo={setAccountSelectionInfo}
        />
      );
    }
  }

  return <div data-testid="consolidate-modal">{currentView}</div>;
};

const field = (name: string) => {
  return yup
    .string()
    .label(name)
    .required()
    .default("");
};

const SelectFormSchema = yup.object({
  fromAccountId: field("ACCOUNT").test({
    name: "mustBeDifferent",
    message: "oops",
    test: function(item: string) {
      return item !== this.parent.toAccountId;
    }
  }),
  toAccountId: field("ACCOUNT").test({
    name: "mustBeDifferent",
    message: "oops",
    test: function(item: string) {
      return item !== this.parent.fromAccountId;
    }
  })
});
export type SelectFormValues = InferType<typeof SelectFormSchema>;

export const SelectView: React.FC<{
  setDisplayMode: SetDisplayModeType;
  accountSelectionInfo: AccountSelectionInfo;
  setAccountSelectionInfo: SetAccountSelectionInfoType;
  accounts: AccountForTable[];
}> = ({
  setDisplayMode,
  accountSelectionInfo,
  setAccountSelectionInfo,
  accounts
}) => {
  const dispatchEvent = useModalDispatch();
  const { t } = useTranslation();
  const placeholder = unknownNamePlaceholder(t);

  // Only admins have permissions to consolidate accounts
  const filteredAccounts = accounts.filter(account => account.role === "admin");

  const handleCancel = () => {
    setAccountSelectionInfo(initialAccountSelectionInfo);
    dispatchEvent();
  };

  const accountNameFromId = (accountId: string) => {
    if (!accountId) {
      return undefined;
    }
    const selectedAccount = filteredAccounts.find(
      account => account.id === accountId
    );
    if (!selectedAccount) {
      return undefined;
    }

    if (selectedAccount.name) {
      return selectedAccount.name;
    }

    return undefined;
  };

  const onSubmit = ({ fromAccountId, toAccountId }: SelectFormValues) => {
    setAccountSelectionInfo({
      fromAccountId: fromAccountId,
      toAccountId: toAccountId,
      accountName: accountNameFromId(toAccountId)
    });
    setDisplayMode(MODE_REVIEW);
  };
  const accountsList = filteredAccounts.map(account => ({
    text: account.name || placeholder,
    value: account.id
  }));

  return (
    <ModalForm<SelectFormValues>
      onSubmit={onSubmit}
      name="consolidate-select"
      initialValues={{
        fromAccountId: accountSelectionInfo.fromAccountId || "",
        toAccountId: accountSelectionInfo.toAccountId || ""
      }}
      validateOnMount
      validationSchema={SelectFormSchema}
      title={`${t("CONSOLIDATE_ACCOUNTS")}`}
      className="fixed-md consolidate-accounts"
    >
      {({ isSubmitting, isValid, values }) => (
        <Form>
          <div className="consolidate-accounts-modal">
            <div className="consolidate-select-view pt-5">
              <div id="subtitle">
                <p className="font-sm">
                  {t("CONSOLIDATE_ACCOUNTS_SELECT_SUBTITLE")}
                </p>
              </div>
              <div className="pt-5 d-flex account-selection">
                <div className="account-col">
                  <h5 className="font-md mb-1">
                    {t("ACCOUNT_TO_CONSOLIDATE")}
                  </h5>
                  <div className="border-radius-8 details-card">
                    <div className="mh-1 pv-1">
                      <StandardFormSelect
                        name="fromAccountId"
                        label={t("ACCOUNT")}
                        options={accountsList}
                        selectOnInput
                        search
                      />
                      <AccountInfoPane tenantId={values.fromAccountId} />
                    </div>
                  </div>
                </div>
                <div className="ph-1 d-flex v-center">
                  <div className="vertical-center">
                    <PolyIcon icon="keyboard_arrow_right" className="white" />
                  </div>
                </div>
                <div className="account-col">
                  <h5 className="font-md mb-1">{t("ACCOUNT_DESTINATION")}</h5>
                  <div className="border-radius-8 details-card">
                    <div className="mh-1 pv-1">
                      <StandardFormSelect
                        name="toAccountId"
                        label={t("ACCOUNT")}
                        options={accountsList}
                        selectOnInput
                        search
                      />
                      <AccountInfoPane tenantId={values.toAccountId} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="btn-container">
              <CancelButton onClick={handleCancel} />
              <SubmitButton
                isSubmitting={isSubmitting}
                testId="select-continue"
                disabled={!isValid}
                labelKey="CONTINUE"
                submittingLabelKey="CONTINUE"
              />
            </div>
          </div>
        </Form>
      )}
    </ModalForm>
  );
};
const ReviewFormSchema = object({
  original: object({
    from: string().required(),
    to: string().required()
  }),
  newAccountName: field("ACCOUNT")
});
type ReviewForm = InferType<typeof ReviewFormSchema>;
export const ReviewView: React.FC<{
  setDisplayMode: SetDisplayModeType;
  accountSelectionInfo: {
    toAccountId: NonNullable<AccountSelectionInfo["toAccountId"]>;
    fromAccountId: NonNullable<AccountSelectionInfo["fromAccountId"]>;
    accountName: NonNullable<AccountSelectionInfo["accountName"]>;
  };
  setAccountSelectionInfo: SetAccountSelectionInfoType;
}> = ({
  setDisplayMode,
  accountSelectionInfo: { fromAccountId, toAccountId, accountName },
  setAccountSelectionInfo
}) => {
  const dispatchEvent = useModalDispatch();
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const { push } = useHistory();
  const [, consolidateAccounts] = useConsolidateAndRenameAccountsMutation();
  const { executor: refreshTenantsList } = useTenantsListQuery(RefreshOnly);
  const [addNotification] = useNotification();
  const { data: fromAccountStatistics } = useDeviceCount(fromAccountId, {
    requestPolicy: "cache-first"
  });
  const { data: toAccountStatistics } = useDeviceCount(toAccountId, {
    requestPolicy: "cache-first"
  });
  const {
    data: usersData,
    fetching: usersFetching
  } = useDualTenantUserCountsQuery({
    variables: {
      tenant1Id: fromAccountId,
      tenant2Id: toAccountId
    }
  });

  const handleBack = () => {
    setDisplayMode(MODE_SELECT);
  };

  const handleCancel = () => {
    setAccountSelectionInfo(initialAccountSelectionInfo);
    dispatchEvent();
  };

  const onSubmit = async ({
    original: { from, to },
    newAccountName
  }: ReviewForm) => {
    const result = await consolidateAccounts(
      {
        fromId: from,
        toId: to,
        input: {
          name: newAccountName
        }
      },
      {
        notify: {
          i18nKey: "CONSOLIDATE_ACCOUNTS_CMD_ERROR",
          dismiss: DISMISS.USER_ONLY
        }
      }
    );

    if (result.error) {
      setDisplayMode(MODE_SELECT);
    } else {
      refreshTenantsList();
      // If we just merged the user's tenant into another tenant, switch them to
      // the successor tenant.
      const extinctTenantId = from;
      const successorTenantId = to;
      let egressTenantId = tenantId;
      if (
        extinctTenantId &&
        successorTenantId &&
        extinctTenantId === tenantId
      ) {
        egressTenantId = successorTenantId;
      }
      addNotification({
        id: "consolidate-accounts-success",
        type: "success",
        message: t("CONSOLIDATE_ACCOUNTS_CMD_SUCCESS"),
        dismiss: DISMISS.SLOW,
        persistThroughNav: true
      });
      push(`/admin/accounts?tenantId=${egressTenantId}`);
    }
  };

  const deviceCount =
    fromAccountStatistics.deviceCount + toAccountStatistics.deviceCount;
  const siteCount =
    fromAccountStatistics.siteCount + toAccountStatistics.siteCount;
  const tenant1Users = usersData?.tenant1?.total || 0;
  const tenant2Users = usersData?.tenant2?.total || 0;
  const commonUsers = usersData?.common?.total || 0;
  const userCount = tenant1Users + tenant2Users - commonUsers;

  return (
    <ModalForm<ReviewForm>
      onSubmit={onSubmit}
      name="consolidate-review"
      initialValues={{
        original: {
          from: fromAccountId,
          to: toAccountId
        },
        newAccountName: accountName
      }}
      validationSchema={ReviewFormSchema}
      title={`${t("CONSOLIDATE_ACCOUNTS")}`}
      className="fixed-md consolidate-accounts"
    >
      {({ isSubmitting, isValid }) => (
        <Form>
          <div className="consolidate-review-view pt-5">
            <div id="subtitle">
              <p className="font-sm">
                {t("CONSOLIDATE_ACCOUNTS_REVIEW_SUBTITLE")}
              </p>
            </div>
            <div>
              <div className="pt-5 account-review">
                <div className="border-radius-4 details-card">
                  <div className="grid mv-15 mh-1 pt-1 details-card-content">
                    <div className="col-6 details-card-input-column">
                      <Input
                        data-testid="account-name"
                        name="newAccountName"
                        label={t("ACCOUNT")}
                      />
                    </div>
                    <div className="col-2">
                      <h6 className="dim font-xs">{t("DEVICES")}</h6>
                      <p>{displayCount(deviceCount)}</p>
                    </div>
                    <div className="col-2">
                      <h6 className="dim font-xs">{t("SITES")}</h6>
                      <p>{displayCount(siteCount)}</p>
                    </div>
                    <div className="col-2">
                      <h6 className="dim font-xs">{t("MEMBERS")}</h6>
                      <p>
                        {usersFetching ? (
                          <PolyIcon
                            icon="refresh"
                            className="spin white icon-xs"
                          />
                        ) : (
                          <>{displayCount(userCount)}</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div id="btn-panel">
              <CancelButton onClick={handleCancel} />
              <button
                className="btn btn-secondary"
                data-testid="review-back"
                type="button"
                onClick={() => handleBack()}
              >
                {t("BACK")}
              </button>
              <SubmitButton
                isSubmitting={isSubmitting}
                testId="review-consolidate"
                disabled={!isValid}
                labelKey="CONSOLIDATE"
                submittingLabelKey="SAVING"
              />
            </div>
          </div>
        </Form>
      )}
    </ModalForm>
  );
};

function useDeviceCount(
  tenantId: string | undefined,
  options?: Omit<UseQueryArgs<any>, "query" | "variables">
) {
  const { data: devicesData, ...rest } = useDeviceCountsQuery({
    variables: {
      filter: {
        AND: constructFilterList({}, tenantId)
      },
      aggregations: [
        {
          term: "site"
        }
      ]
    },
    ...options
  });
  if (tenantId) {
    if (
      devicesData?.deviceAggregations?.[0]?.buckets &&
      isArrayOfGenericBucket(devicesData.deviceAggregations[0].buckets)
    ) {
      let devices: Array<GenericBucket> =
        devicesData.deviceAggregations[0].buckets;
      const siteCount = devices.length;
      const deviceCount = devices.reduce(
        (count, device) => count + device.count,
        0
      );
      return {
        data: { siteCount, deviceCount },
        ...rest
      };
    }
  }
  return {
    data: { siteCount: NO_COUNT, deviceCount: NO_COUNT },
    ...rest
  };
}

function useUserCount(
  tenantId: string,
  options?: Omit<UseQueryArgs<any>, "query" | "variables">
) {
  const { data: usersData, fetching } = useUsersListQuery({
    variables: {
      params: {
        grants: [
          {
            resourceId: tenantId
          }
        ]
      }
    },
    ...options
  });
  return {
    userCount: usersData?.searchResult?.total ?? NO_COUNT,
    fetching
  };
}

const AccountInfoPane: React.FC<{
  tenantId?: string;
}> = ({ tenantId }) => {
  const { t } = useTranslation();
  const {
    data: { siteCount, deviceCount },
    fetching: deviceFetching
  } = useDeviceCount(tenantId);

  return (
    <div className="pt-5">
      <h6 className="dim font-xs">{t("DEVICES")}</h6>
      <p>
        {deviceFetching ? (
          <PolyIcon icon="refresh" className="spin white icon-xs" />
        ) : (
          <>{displayCount(deviceCount)}</>
        )}
      </p>
      <h6 className="dim font-xs">{t("SITES")}</h6>
      <p>
        {deviceFetching ? (
          <PolyIcon icon="refresh" className="spin white icon-xs" />
        ) : (
          <>{displayCount(siteCount)}</>
        )}
      </p>
      <h6 className="dim font-xs">{t("MEMBERS")}</h6>
      <p>
        {tenantId ? (
          <UserAccountCount tenantId={tenantId} />
        ) : (
          <>{displayCount(NO_COUNT)}</>
        )}
      </p>
    </div>
  );
};

const UserAccountCount: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const { userCount, fetching: usersFetching } = useUserCount(tenantId ?? "");

  if (usersFetching) {
    return <PolyIcon icon="refresh" className="spin white icon-xs" />;
  }
  return <>{displayCount(userCount)}</>;
};

export default ConsolidateAccounts;
